import React, { useState, useEffect, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import TableIndex from '@components/shared/table/TableIndex';
import type { TableService } from '@components/shared/table/TableIndex';
import useBatchOperation from '@hooks/shared/useBatchOperation';
import useSmartBack from '@hooks/shared/useSmartBack';
import { useBackgroundJob } from '@shared/BackgroundJobManager';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import {
  handleApiError as handleApiErrorRaw,
  handleNetworkError as handleNetworkErrorRaw,
} from '@utils/apiErrorHandler';

const handleApiError = handleApiErrorRaw as (response: unknown, options?: Record<string, unknown>) => void;
const handleNetworkError = handleNetworkErrorRaw as (error: unknown, options?: Record<string, unknown>) => void;
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker';
import { Card, Descriptions, Tag, Space, Button, message, Modal, Empty, Form, Input, Select, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { warehouseZoneService } from '@services/operator/warehouseZoneService';
import { formatDateTime } from '../../../utils/dateTime';
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';

const TEMPERATURE_OPTIONS = ['ambient', 'controlled', 'refrigerated', 'frozen', 'air_conditioned'];

interface ShelfData {
  id: string;
  location?: string;
  shelf_type_id?: string;
  shelf_type?: { name?: string } | null;
  temperature?: string;
  warehouse?: { name?: string } | null;
  warehouse_zone?: { name?: string } | null;
  warehouse_zone_id?: string | null;
  pick_face?: boolean;
  locked?: boolean;
  capacity?: number;
  created_at?: string;
  editable?: boolean;
  deletable?: boolean;
  [key: string]: unknown;
}

interface ShelfTypeOption {
  id: string;
  name: string;
}

interface WarehouseZoneOption {
  id: string;
  name: string;
}

interface ShelfServiceContract extends TableService {
  update: (id: string, values: Record<string, unknown>) => Promise<{ success: boolean; data?: ShelfData; message?: string; [key: string]: unknown }>;
  // Operator only — async batch delete via worker; passing a single id reuses the
  // batch endpoint's FK-safety pipeline without needing a separate destroy route.
  // Admin's shelfService doesn't expose this (admin can't delete shelves).
  batchDelete?: (shelfIds: string[]) => Promise<{ success: boolean; data?: { job_id?: string }; message?: string; [key: string]: unknown }>;
  getInventories: (payload: Record<string, unknown>) => Promise<{ data: unknown[]; has_more: boolean }>;
  getInventoryIds: (payload: Record<string, unknown>) => Promise<{ data: (string | number)[] }>;
}

interface InventoryRow {
  id: string;
  inventory?: {
    product?: { id: string; name?: string; sku?: string } | null;
    batch?: string | null;
    custom_attributes?: Record<string, unknown> | null;
    manufacturing_date?: string | null;
    expiration_date?: string | null;
  } | null;
  quantity?: number;
  created_at?: string;
  [key: string]: unknown;
}

interface ShelfShowViewProps {
  shelf: ShelfData | null | undefined;
  role?: 'admin' | 'operator';
  shelfService: ShelfServiceContract;
  shelfTypes?: ShelfTypeOption[];
}

const ShelfShowView: React.FC<ShelfShowViewProps> = ({
  shelf: initialShelf,
  role = 'admin',
  shelfService,
  shelfTypes = []
}) => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const [lastModified, setLastModified] = useState(Date.now());
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shelf, setShelf] = useState<ShelfData | null | undefined>(initialShelf);
  const [warehouseZones, setWarehouseZones] = useState<WarehouseZoneOption[]>([]);

  const { addJob } = useBackgroundJob();
  const executeBatchOperation = useBatchOperation(shelfService, addJob);

  useEffect(() => {
    if (initialShelf) {
      setShelf(initialShelf);
      form.setFieldsValue({
        ...initialShelf,
        pick_face: initialShelf.pick_face ?? false,
        locked: initialShelf.locked ?? false
      });
    }
  }, [initialShelf, form]);

  useEffect(() => {
    if (role === 'operator') {
      warehouseZoneService.getRecords().then((res: { data?: WarehouseZoneOption[] }) => {
        if (res.data) {
          setWarehouseZones(res.data);
        }
      }).catch(() => { });
    }
  }, [role]);

  const handleEdit = () => {
    if (!shelf) return;
    form.setFieldsValue({
      location: shelf.location,
      shelf_type_id: shelf.shelf_type_id,
      temperature: shelf.temperature,
      warehouse_zone_id: shelf.warehouse_zone_id || undefined,
      pick_face: shelf.pick_face ?? false,
      locked: shelf.locked ?? false
    });
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    form.setFieldsValue(shelf);
  };

  const handleSave = async (values: Record<string, unknown>) => {
    if (!shelf) return;
    setSaving(true);
    try {
      const response = await shelfService.update(shelf.id, values);
      if (response.success) {
        message.success(t('common.message.update_success'));
        if (response.data) setShelf(response.data);
        setIsEditMode(false);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.update_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Update shelf error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!shelf || !shelfService.batchDelete) return;
    const shelfBatchDelete = shelfService.batchDelete;
    Modal.confirm({
      title: t('shared.shelves.list.batch_delete.confirm_title') as string,
      content: t('shared.shelves.list.batch_delete.confirm_body', { count: 1 }) as string,
      okText: t('shared.shelves.list.batch_delete.confirm_ok') as string,
      okType: 'danger',
      cancelText: t('common.cancel') as string,
      onOk: async () => {
        // Worker is async; navigate back as soon as the job is enqueued.
        // The BackgroundJobManager panel + toast will surface the result
        // (deleted / skipped counts) from the list page.
        await executeBatchOperation({
          task: () => shelfBatchDelete([shelf.id]),
          jobName: 'shared.shelves.list.actions.batch_delete',
        });
        goBack();
      },
    });
  };

  const getTemperatureTag = (temperature: string | undefined): ReactNode => {
    const temperatureMap: Record<string, { color: string; text: string }> = {
      ambient: { color: 'default', text: t('enums.temperature.ambient') },
      controlled: { color: 'blue', text: t('enums.temperature.controlled') },
      refrigerated: { color: 'cyan', text: t('enums.temperature.refrigerated') },
      frozen: { color: 'geekblue', text: t('enums.temperature.frozen') },
      air_conditioned: { color: 'purple', text: t('enums.temperature.air_conditioned') }
    };
    const config = (temperature && temperatureMap[temperature]) || { color: 'default', text: temperature || '' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getStatusTag = (status: string | undefined): ReactNode => {
    const statusMap: Record<string, { color: string; text: string }> = {
      in_stock: { color: 'success', text: t('enums.inventory.status.in_stock') },
      out_of_stock: { color: 'default', text: t('enums.inventory.status.out_of_stock') },
      depleted: { color: 'error', text: t('enums.inventory.status.depleted') }
    };
    const config = (status && statusMap[status]) || { color: 'default', text: status || '' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 篩選規則配置
  const supportRules = useMemo(() => [
    {
      label: t('models.product.sku'),
      component: MultipleValue,
      inputName: 'products.sku',
      createable: true,
    },
    {
      label: t('models.inventory.batch'),
      component: MultipleValue,
      inputName: 'inventories.batch',
      createable: true,
    },
    {
      label: t('models.inventory.expiration_date'),
      component: FullDayRangePicker,

      inputName: 'inventories.expiration_date',
    },
  ], [t]);

  // TableIndex columns configuration
  const columns = useMemo(() => [
    {
      nameKey: 'models.product.name',
      persist: true,
      sortable: false,
      width: '200px',
      wrap: true,
      cell: ({ inventory }: InventoryRow) => inventory?.product ? (
        <Link to={`/products/${inventory.product.id}`}>
          {inventory.product.name}
        </Link>
      ) : '-',
    },
    {
      nameKey: 'models.product.sku',
      sortable: false,
      width: '150px',
      wrap: true,
      selector: ({ inventory }: InventoryRow) => inventory?.product?.sku || '-',
    },
    {
      nameKey: 'models.inventory.batch',
      sortable: false,
      width: '150px',
      wrap: true,
      selector: ({ inventory }: InventoryRow) => inventory?.batch || '-',
    },
    {
      nameKey: 'models.inventory.custom_attributes',
      sortable: false,
      width: '200px',
      wrap: true,
      cell: ({ inventory }: InventoryRow) => (
        <CustomAttributesTags attributes={inventory?.custom_attributes} />
      )
    },
    {
      nameKey: 'models.inventory.manufacturing_date',
      sortable: false,
      width: '120px',
      selector: ({ inventory }: InventoryRow) => inventory?.manufacturing_date || '-',
    },
    {
      nameKey: 'models.inventory.expiration_date',
      sortable: false,
      width: '120px',
      selector: ({ inventory }: InventoryRow) => inventory?.expiration_date || '-',
    },
    {
      nameKey: 'models.inventory.quantity',
      sortable: false,
      width: '100px',
      cell: ({ quantity }: InventoryRow) => quantity?.toLocaleString() || 0,
    },
    {
      nameKey: 'common.created_at',
      sortable: false,
      width: '120px',
      selector: ({ created_at }: InventoryRow) => created_at,
    },
  ], [role, t]);

  if (!shelf) {
    return (
      <Empty description={t('common.error.no_data')} />
    );
  }

  const temperatureOptions = TEMPERATURE_OPTIONS.map(temp => ({
    value: temp,
    label: t(`enums.temperature.${temp}`)
  }));

  const shelfTypeOptions = shelfTypes.map((st) => ({
    value: st.id,
    label: st.name
  }));

  const warehouseZoneOptions = warehouseZones.map((wz) => ({
    value: wz.id,
    label: wz.name
  }));

  const basePath = role === 'operator' ? '/operator' : '/admin';
  const breadcrumbItems = role === 'operator'
    ? ['stocks', 'stocks.shelves', { label: shelf.location || '' }]
    : ['data', 'data.shelves', { label: shelf.location || '' }];

  const headerActions = !isEditMode && (
    <Space>
      {shelf.editable && (
        <Can requiredPermission="shelves:write">
          <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
            {t('common.edit')}
          </Button>
        </Can>
      )}
      {shelf.deletable && shelfService.batchDelete && (
        <Can requiredPermission="shelves:write">
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('common.delete')}
          </Button>
        </Can>
      )}
    </Space>
  );

  return (
    <div>
      <ListPageHeader
        basePath={basePath}
        items={breadcrumbItems}
        actions={headerActions}
      />
      {isEditMode ? (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={shelf}
        >
          <Form.Item
            label={t('models.shelf.location')}
            name="location"
            rules={[{ required: true, message: t('common.form.required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('models.shelf.shelf_type')}
            name="shelf_type_id"
            rules={[{ required: true, message: t('common.form.required') }]}
          >
            <Select options={shelfTypeOptions} />
          </Form.Item>

          <Form.Item
            label={t('models.shelf.temperature')}
            name="temperature"
            rules={[{ required: true, message: t('common.form.required') }]}
          >
            <Select options={temperatureOptions} />
          </Form.Item>

          {warehouseZoneOptions.length > 0 && (
            <Form.Item
              label={t('models.shelf.warehouse_zone')}
              name="warehouse_zone_id"
            >
              <Select options={warehouseZoneOptions} allowClear />
            </Form.Item>
          )}

          <Form.Item
            name="pick_face"
            valuePropName="checked"
          >
            <Checkbox>{t('models.shelf.pick_face')}</Checkbox>
          </Form.Item>

          <Form.Item
            name="locked"
            valuePropName="checked"
          >
            <Checkbox>{t('models.shelf.locked')}</Checkbox>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={saving}
              >
                {t('common.button.save')}
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancel}
                disabled={saving}
              >
                {t('common.button.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      ) : (
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t('models.shelf.location')}>
            {shelf.location}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.warehouse.name')}>
            {shelf.warehouse?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.shelf.shelf_type')}>
            {shelf.shelf_type?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.shelf.temperature')}>
            {getTemperatureTag(shelf.temperature)}
          </Descriptions.Item>
          {(shelf.warehouse_zone || warehouseZoneOptions.length > 0) && (
            <Descriptions.Item label={t('models.shelf.warehouse_zone')}>
              {shelf.warehouse_zone?.name || '-'}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t('models.shelf.capacity')}>
            {shelf.capacity?.toLocaleString() || 0} {t('models.inventory.quantity')}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.shelf.pick_face')}>
            {shelf.pick_face ? <Tag color="success">{t('common.yes')}</Tag> : <Tag>{t('common.no')}</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.shelf.locked')}>
            {shelf.locked ? <Tag color="error">{t('common.yes')}</Tag> : <Tag>{t('common.no')}</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.created_at')}>
            {shelf.created_at ? formatDateTime(shelf.created_at) : '-'}
          </Descriptions.Item>
        </Descriptions>
      )}

      <Card
        title={t('shared.shelves.show.inventory_list')}
        style={{ marginTop: 16 }}
      >
        <TableIndex
          service={{
            getRecords: shelfService.getInventories,
            getIds: shelfService.getInventoryIds
          }}
          columns={columns}
          defaultQuery={{ shelf_id: shelf.id }}
          tableId={`shelf_${shelf.id}_inventories`}
          columnsEditable={true}
          supportRules={supportRules}
          lastModified={lastModified}
          setLastModified={setLastModified}
          filterHelpExample={t('shared.shelves.show.filter_help_example') as string}
          filterDocsUrl={`/docs/${role}/shelves/list#features`}
        />
      </Card>
    </div>
  );
};

export default ShelfShowView;
